import {FontAwesomeIcon as Icon} from "@fortawesome/react-fontawesome"
import {Auction} from "domain-models"
import React from "react"
import AuctionCard from "../Auctions/AuctionCard"
import Trans from '../../components/Trans'
import {SpeedAuktionRound} from "./SpeedAuktionDetails"

type SpeedAuktionCardProps = {
  round: SpeedAuktionRound
  onShowDetails: (round: SpeedAuktionRound) => void
  onClaim: (roundId: number) => void
  claiming: boolean
}

class SpeedAuktionCard extends React.Component<SpeedAuktionCardProps> {
  
  isExpired(): boolean {
    const endsOn = this.props.round.SpeedAuktionEndsOn
    if (!endsOn) return false
    return new Date(endsOn) < new Date()
  }

  getHighestBid(): number | null {
    const round = this.props.round
    // Try highest_bid first (from API), then bids array
    if (round.highest_bid?.Bid) {
      return round.highest_bid.Bid
    }
    if (round.bids && round.bids.length > 0) {
      return Math.max(...round.bids.map(b => b.Bid))
    }
    return round.winner?.Bid || null
  }

  handleShowDetails = (event: React.MouseEvent, auction: Auction) => {
    this.props.onShowDetails(this.props.round)
  }

  handleClaim = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    this.props.onClaim(this.props.round.Id)
  }

  renderBadge = () => {
    return (
      <div className="px-2 py-1">
        <span className="px-2 py-1 bg-yellow-100 border border-yellow-400 rounded inline-block">
          <Icon icon="bolt" className="text-yellow-500"/>
          <span className="ml-1 text-yellow-700 font-semibold text-sm">Speed-Auktion</span>
        </span>
      </div>
    )
  }

  renderPrice = (auction: Auction) => {
    const highestBid = this.getHighestBid()
    if (!highestBid) return null
    
    // Get vehicle to check VAT status
    const subject = auction.subjects?.[0]
    const vehicle = subject?.vehicle
    
    // Check if vehicle is vatable (SellFlags bit 32768 = MwSt. ausweisbar)
    const isVatable = vehicle && (vehicle.SellFlags & 32768) !== 0
    
    // Calculate net price if vatable (divide by 1.19)
    const displayPrice = isVatable ? highestBid / 1.19 : highestBid
    
    // Determine VAT label
    const vatLabel = isVatable 
      ? 'Price netto / exclusive VAT' 
      : '§25 VAT not deductible'
    
    return (
      <div className="px-2 py-2">
        <div className="p-2 bg-green-50 rounded">
          <div className="flex flex-col">
            <span className="text-sm text-gray-600"><Trans>Preis</Trans>:</span>
            <span className="text-xl font-bold text-green-600">
              {new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(displayPrice)}
            </span>
            <span className="text-xs text-gray-500 mt-1">{vatLabel}</span>
          </div>
        </div>
      </div>
    )
  }

  renderActions = (auction: Auction) => {
    const isExpired = this.isExpired()
    const claiming = this.props.claiming

    return (
      <div className="p-2 mt-auto">
        <button
          onClick={this.handleClaim}
          disabled={claiming || isExpired}
          className={`w-full py-3 rounded font-semibold transition-colors ${
            isExpired 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {claiming ? (
            <><Icon icon="spinner" spin/> <Trans>Wird beansprucht...</Trans></>
          ) : isExpired ? (
            <Trans>Abgelaufen</Trans>
          ) : (
            <><Icon icon="hand-paper"/> <Trans>Sofort Kauf</Trans></>
          )}
        </button>
      </div>
    )
  }

  render() {
    const {round} = this.props

    if (!round.auction) {
      return null
    }

    return (
      <AuctionCard
        auction={round.auction}
        onShowDetails={this.handleShowDetails}
        badgeContent={this.renderBadge()}
        countdownEndDate={round.SpeedAuktionEndsOn}
        renderPrice={this.renderPrice}
        renderActions={this.renderActions}
      />
    )
  }
}

export default SpeedAuktionCard
